import type { ViewHook } from "phoenix_live_view";
import { HLSDownloader } from "../utils/hls-download.js";
import type { DownloadProgress, DownloadState } from "../utils/hls-download.js";

interface HLSDownloadHook extends ViewHook {
  __beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(1) + " " + units[i];
}

function formatSpeed(bytesPerSecond: number): string {
  return formatBytes(bytesPerSecond) + "/s";
}

type ButtonVisibility = {
  pause: boolean;
  resume: boolean;
  stop: boolean;
};

function getButtonVisibility(state: DownloadState): ButtonVisibility {
  return {
    pause: state === "downloading",
    resume: state === "paused",
    stop: state === "downloading" || state === "paused",
  };
}

export const HLSDownload = {
  __beforeUnloadHandler: null as ((e: BeforeUnloadEvent) => void) | null,

  destroyed(this: HLSDownloadHook) {
    if (this.__beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.__beforeUnloadHandler);
      this.__beforeUnloadHandler = null;
    }
  },

  mounted(this: HLSDownloadHook) {
    const button = this.el.querySelector<HTMLButtonElement>("[data-hls-download-btn]");
    const progressContainer = this.el.querySelector<HTMLDivElement>("[data-hls-progress]");
    const pauseBtn = this.el.querySelector<HTMLButtonElement>("[data-hls-pause-btn]");
    const resumeBtn = this.el.querySelector<HTMLButtonElement>("[data-hls-resume-btn]");
    const stopBtn = this.el.querySelector<HTMLButtonElement>("[data-hls-stop-btn]");

    if (!button || !progressContainer) return;

    const url = this.el.dataset.url;
    const filename = this.el.dataset.filename || "recording.mp4";

    if (!url) return;

    // Notification toggle
    const notifyToggle = this.el.querySelector<HTMLLabelElement>("[data-hls-notify-toggle]");
    const notifyCheckbox = this.el.querySelector<HTMLInputElement>("[data-hls-notify-checkbox]");

    if (notifyToggle && notifyCheckbox && "Notification" in window) {
      notifyToggle.hidden = false;

      notifyCheckbox.addEventListener("change", async () => {
        if (notifyCheckbox.checked && Notification.permission === "default") {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") {
            notifyCheckbox.checked = false;
          }
        }
      });
    }

    let downloader: HLSDownloader | null = null;

    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (downloader?.isActive) {
        e.preventDefault();
      }
    };
    this.__beforeUnloadHandler = beforeUnloadHandler;
    window.addEventListener("beforeunload", beforeUnloadHandler);

    const updateButtons = (state: DownloadState) => {
      const visibility = getButtonVisibility(state);
      if (pauseBtn) {
        pauseBtn.hidden = !visibility.pause;
      }
      if (resumeBtn) {
        resumeBtn.hidden = !visibility.resume;
      }
      if (stopBtn) {
        stopBtn.hidden = !visibility.stop;
      }
    };

    const updateProgress = (progress: DownloadProgress) => {
      if (progress.state === "idle") {
        progressContainer.classList.add("hidden");
        button.disabled = false;
        return;
      }

      progressContainer.classList.remove("hidden");
      updateButtons(progress.state);

      const messageEl = progressContainer.querySelector("[data-progress-message]");
      const barEl = progressContainer.querySelector<HTMLDivElement>("[data-progress-bar]");

      if (barEl) {
        const percent =
          progress.totalSegments > 0 ? (progress.currentSegment / progress.totalSegments) * 100 : 0;
        barEl.style.width = `${percent}%`;
      }

      if (messageEl) {
        const parts: string[] = [progress.message];
        if (progress.bytesDownloaded > 0) {
          parts.push(formatBytes(progress.bytesDownloaded));
        }
        if (progress.speed > 0 && progress.state === "downloading") {
          parts.push(formatSpeed(progress.speed));
        }
        messageEl.textContent = parts.join("  ·  ");
        messageEl.classList.remove("text-red-600");
        messageEl.classList.add("text-gray-500");
      }

      if (progress.state === "complete" || progress.state === "stopped") {
        button.disabled = false;
      }
    };

    const showCompletionInfo = (savedFilename: string) => {
      const messageEl = progressContainer.querySelector("[data-progress-message]");
      if (messageEl) {
        messageEl.textContent = `Saved as ${savedFilename}`;
        messageEl.classList.remove("text-red-600");
        messageEl.classList.add("text-gray-500");
      }

      const barEl = progressContainer.querySelector<HTMLDivElement>("[data-progress-bar]");
      if (barEl) {
        barEl.classList.remove("bg-indigo-600");
        barEl.classList.add("bg-green-500");
      }
    };

    const startDownload = async () => {
      if (downloader?.isActive) return;

      button.disabled = true;

      // Reset bar color for new downloads
      const barEl = progressContainer.querySelector<HTMLDivElement>("[data-progress-bar]");
      if (barEl) {
        barEl.classList.remove("bg-green-500");
        barEl.classList.add("bg-indigo-600");
      }

      downloader = new HLSDownloader(
        {
          onProgress: updateProgress,
          onError: (error: Error) => {
            button.disabled = false;
            updateButtons("error");
            const messageEl = progressContainer.querySelector("[data-progress-message]");
            if (messageEl) {
              messageEl.textContent = `Error: ${error.message}`;
              messageEl.classList.add("text-red-600");
              messageEl.classList.remove("text-gray-500");
            }
          },
          onComplete: (savedFilename: string) => {
            button.disabled = false;
            showCompletionInfo(savedFilename);
            if (notifyCheckbox?.checked && Notification.permission === "granted") {
              const notification = new Notification("Recording download complete", {
                body: `Saved as ${savedFilename}`,
                tag: "hls-download-complete",
                requireInteraction: true,
              });
              notification.onclick = () => {
                window.focus();
                notification.close();
              };
            }
          },
        },
        "h264",
      );

      await downloader.download(url, filename);
    };

    button.addEventListener("click", startDownload);

    if (pauseBtn) {
      pauseBtn.addEventListener("click", () => {
        downloader?.pause();
      });
    }

    if (resumeBtn) {
      resumeBtn.addEventListener("click", () => {
        downloader?.resume();
      });
    }

    if (stopBtn) {
      stopBtn.addEventListener("click", () => {
        downloader?.stop();
      });
    }
  },
};
